import type { Graph, Organization, WebSite } from 'schema-dts'
import { KONFETTI_COMPANY as company } from '~~/constants/company'

export const ORGANIZATION_ID = `${company.url}#organization`
export const WEBSITE_ID = `${company.url}#website`

// The organization as publisher on other pages: the full node only lives on
// the homepage, so carry the fields Google needs inline next to the @id.
export const organizationReference: Organization = {
  '@type': 'Organization',
  '@id': ORGANIZATION_ID,
  name: company.name,
  url: company.url,
  logo: { '@type': 'ImageObject', url: company.logoUrl },
}

// Both locales describe the same site and company under the same @id, so the
// url is the domain root (Google reads site names per domain, not per
// language folder) and only the description is localized.
export const createOrganizationSchema = (description: string): Graph => {
  const organization: Organization = {
    ...organizationReference,
    legalName: company.legalName,
    description,
    email: company.email,
    telephone: company.telephone,
    address: { '@type': 'PostalAddress', ...company.address },
    vatID: company.vatId,
    identifier: {
      '@type': 'PropertyValue',
      propertyID: 'Handelsregister',
      value: company.commercialRegister,
    },
    sameAs: [...company.socialProfiles],
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: company.telephone,
        email: company.email,
        contactType: 'customer service',
        availableLanguage: [...company.languages],
      },
    ],
  }

  // Drives the site name Google shows above search results.
  const website: WebSite = {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    name: company.name,
    alternateName: company.alternateName,
    url: company.url,
    inLanguage: ['de-DE', 'en-US'],
    publisher: { '@id': ORGANIZATION_ID },
  }

  return {
    '@context': 'https://schema.org',
    '@graph': [website, organization],
  }
}

export const useOrganizationSchema = () => {
  const { t } = useI18n()

  useHead({
    script: [
      {
        key: 'organization-schema',
        type: 'application/ld+json',
        innerHTML: computed(() =>
          JSON.stringify(
            createOrganizationSchema(t('seo.organization.description'))
          )
        ),
      },
    ],
  })
}
